import React from 'react';
import { Box, Typography, Button, Theme } from '@material-ui/core';
import { ChevronRight, Visibility, VisibilityOff, Link as LinkIcon, Assessment, Settings } from '@material-ui/icons';
import { makeStyles, createStyles } from '@material-ui/styles';
import { useHistory } from 'react-router';
import Icon from '@mdi/react';
import { mdiQrcode } from '@mdi/js';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { FormQRCodePrintPage } from './FormQRCodePrintPage';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      alignItems: 'center',
      paddingRight: '24px',
      paddingLeft: '24px',
      paddingTop: '16px',
      paddingBottom: '16px',
      border: '1px solid #54545440',
      borderRadius: '4px',
      justifyContent: 'space-between',
      cursor: 'pointer',
      backgroundColor: theme.color.white.neutral50,
    },
    formContainer: {
      '& .title': {
        fontSize: '24px',
        fontWeight: 800,
        color: theme.color.grey.neutral400,
      },
      '& .description': {
        fontWeight: 400,
        color: theme.color.grey.neutral400,
        fontSize: '0.9rem',
      },
      '& .badge': {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: '0.75rem',
        fontWeight: 550,
        padding: 8,
        paddingLeft: 12,
        paddingRight: 12,
        borderRadius: 4,
        backgroundColor: '#D9D9D9',
        color: theme.color.grey.neutral400,
        whiteSpace: 'nowrap',
      },
      '& .badgeContainer': {
        display: 'flex',
        flexWrap: 'wrap',
        marginTop: '8px',
        gap: '8px',
      },
    },
    button: {
      paddingLeft: 0,
      paddingRight: 0,
      minWidth: 'unset',
      width: '36px',
      color: 'white',
      backgroundColor: '#9E9E9E',
    },
  })
);

interface FormProps {
  form: any;
  orgLogo?: string | null;
  onClick: () => void;
}

export const Form: React.FC<FormProps> = ({ form, orgLogo, onClick }) => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const { setSnackbar } = useSnackbar();
  const history = useHistory();

  const isClosed = form.dateClosed && new Date(form.dateClosed) <= new Date();

  const handleViewResponses = (e: React.MouseEvent) => {
    e.stopPropagation();
    history.push(`/admin/forms/responses?fid=${form.formId}`);
  };

  const handleSettings = (e: React.MouseEvent) => {
    e.stopPropagation();
    history.push(`/admin/forms/responses?fid=${form.formId}&tab=settings`);
  };

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    const formUrl = `${window.location.origin}/form/${form.formId}`;
    navigator.clipboard.writeText(formUrl).then(() => {
      setSnackbar({
        open: true,
        message: 'Link copied to clipboard',
        variant: 'success',
      });
    });
  };

  return (
    <Box className={classes.container} onClick={onClick}>
      <Box className={classes.formContainer}>
        <Box style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
          <Typography
            variant={'h6'}
            style={{ color: theme.color.grey.neutral400, fontWeight: 700, letterSpacing: '-0.02em', flexShrink: 0 }}
          >
            {form.title}
          </Typography>
          {form.description && (
            <Typography
              className='description'
              style={{
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {form.description}
            </Typography>
          )}
        </Box>
        <Box className={'badgeContainer'} style={{ alignItems: 'center' }}>
          {form.isListed ? (
            <Box
              className='badge'
              style={{
                backgroundColor: `${theme.alert.green.light}20`,
                color: theme.alert.green.dark,
              }}
            >
              <Visibility style={{ fontSize: '14px', marginRight: '6px' }} />
              Public
            </Box>
          ) : (
            <Box
              className='badge'
              style={{
                backgroundColor: `${theme.alert.orange.light}20`,
                color: theme.alert.orange.dark,
              }}
            >
              <VisibilityOff style={{ fontSize: '14px', marginRight: '6px' }} />
              Private
            </Box>
          )}
          {isClosed ? (
            <Box
              className='badge'
              style={{
                backgroundColor: `${theme.alert.red.light}20`,
                color: theme.alert.red.dark,
              }}
            >
              Closed
            </Box>
          ) : (
            <Box
              className='badge'
              style={{
                backgroundColor: `${theme.alert.blue.light}20`,
                color: theme.alert.blue.dark,
              }}
            >
              Open
            </Box>
          )}
          {!isClosed && (
            <>
              <Typography
                style={{
                  fontSize: '0.7rem',
                  color: theme.color.grey.neutral300,
                  marginLeft: '12px',
                  marginRight: '8px',
                }}
              >
                |
              </Typography>
              <Typography
                style={{
                  fontSize: '0.7rem',
                  color: theme.color.grey.neutral300,
                  marginRight: '8px',
                }}
              >
                Quick Actions
              </Typography>
              <Box
                className='badge'
                onClick={handleViewResponses}
                style={{
                  backgroundColor: `${theme.color.grey.neutral200}40`,
                  color: theme.color.grey.neutral400,
                  cursor: 'pointer',
                }}
              >
                <Assessment style={{ fontSize: '14px', marginRight: '6px' }} />
                Responses
              </Box>
              <Box
                className='badge'
                onClick={handleCopyLink}
                style={{
                  backgroundColor: `${theme.color.grey.neutral200}40`,
                  color: theme.color.grey.neutral400,
                  cursor: 'pointer',
                }}
              >
                <LinkIcon style={{ fontSize: '14px', marginRight: '6px' }} />
                Copy Link
              </Box>
              <FormQRCodePrintPage
                formId={form.formId}
                formTitle={form.title}
                orgLogo={orgLogo}
                trigger={() => (
                  <Box
                    className='badge'
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    style={{
                      backgroundColor: `${theme.color.grey.neutral200}40`,
                      color: theme.color.grey.neutral400,
                      cursor: 'pointer',
                    }}
                  >
                    <Icon path={mdiQrcode} size={0.6} style={{ marginRight: '6px' }} />
                    QR Code
                  </Box>
                )}
              />
              <Box
                className='badge'
                onClick={handleSettings}
                style={{
                  backgroundColor: `${theme.color.grey.neutral200}40`,
                  color: theme.color.grey.neutral400,
                  cursor: 'pointer',
                }}
              >
                <Settings style={{ fontSize: '14px', marginRight: '6px' }} />
                Settings
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Button variant='contained' disableElevation className={classes.button}>
        <ChevronRight />
      </Button>
    </Box>
  );
};
